/**
 * Shared markup + validation for the player add/edit modals, which use an
 * identical name + level form. Keeps a single source of truth for the form so
 * the two modals don't drift.
 */

interface PlayerFormCardOptions {
  title: string;
  formId: string;
  nameLabel: string;
  namePlaceholder?: string;
  nameRequired?: boolean;
  submitIcon: string;
  submitLabel: string;
}

/** Render the modal card (title + name/level form) shared by both player modals. */
export const renderPlayerFormCard = (o: PlayerFormCardOptions): string => `
      <div class="bg-white dark:bg-surface-dark daylight:bg-day-raised rounded-2xl shadow-xl daylight:shadow-day-modal p-8 max-w-md w-full mx-4 border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-6">${o.title}</h2>

        <form id="${o.formId}" class="space-y-4">
          <div>
            <label class="block text-sm font-bold text-slate-900 dark:text-white mb-2">${o.nameLabel}</label>
            <input
              id="playerName"
              type="text"${o.namePlaceholder ? `\n              placeholder="${o.namePlaceholder}"` : ""}
              class="input-field w-full"${o.nameRequired ? `\n              required` : ""}
            />
            <p class="mt-2 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
              <span class="material-symbols-outlined text-sm leading-none" style="vertical-align: -2px;">shield</span>
              <span>For better privacy, you can use aliases or initials (e.g., Carlos T. / Virginia M.)</span>
            </p>
          </div>

          <div>
            <label class="block text-sm font-bold text-slate-900 dark:text-white mb-2">Level</label>
            <select id="playerLevel" class="input-field w-full">
              <option value="0">0 - Beginner</option>
              <option value="1">1 - Novice</option>
              <option value="2">2 - Intermediate</option>
              <option value="3">3 - Advanced</option>
              <option value="4">4 - Expert</option>
              <option value="5">5 - Pro</option>
            </select>
          </div>

          <div id="errorMessage" class="hidden p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm border border-red-200 dark:border-red-800"></div>

          <div class="flex gap-3 pt-4">
            <button
              id="cancelBtn"
              type="button"
              class="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              id="submitBtn"
              type="submit"
              class="btn-primary flex-1"
            >
              <span class="material-symbols-outlined">${o.submitIcon}</span>
              ${o.submitLabel}
            </button>
          </div>
        </form>
      </div>
    `;

/** Validate the player form. Returns an error message, or null when valid. */
export const validatePlayerForm = (name: string, level: number): string | null => {
  if (!name) return "Name is required";
  if (isNaN(level) || level < 0 || level > 5) return "Level must be between 0 and 5";
  return null;
};
